"use client";

import EditableFlashcardCard from "@/components/flashcards/flashcardsCard/EditableFlashcardCard";
import { FlexColumn } from "@/components/Flex";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import {
    closestCenter,
    DndContext,
    DraggableAttributes,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { createContext, useContext, useState } from "react";
import { FlashcardSortable } from "../types";

export const SetEditingDndContext = ({
    children,
    onDragEnd,
}: {
    children: React.ReactNode;
    onDragEnd: (props: { active: FlashcardSortable; over: FlashcardSortable }) => void | Promise<void>;
}) => {
    const sensors = useSensors(useSensor(PointerSensor));
    const [currDraggedItem, setCurrDraggedItem] = useState<FlashcardSortable | null>(null);

    return (
        <DndContext
            collisionDetection={closestCenter}
            onDragStart={event => {
                const draggedItem = event.active.data.current;
                setCurrDraggedItem(draggedItem as FlashcardSortable);
            }}
            onDragEnd={async event => {
                if (!event.over || !event.active) return;

                const droppableItem = event.over.data.current as FlashcardSortable;

                if (!currDraggedItem) {
                    throw new Error("currDraggedItem is null");
                }

                await onDragEnd({ active: currDraggedItem, over: droppableItem });
            }}
            sensors={sensors}>
            {children}

            <DragOverlay dropAnimation={null}>
                {(() => {
                    if (!currDraggedItem) return null;
                    const flashcard = currDraggedItem;

                    return (
                        <EditableFlashcardCard
                            flashcardSetTitle={""}
                            flashcard={flashcard}
                            index={flashcard.index}
                            disableDelete={true}
                        />
                    );
                })()}
            </DragOverlay>
        </DndContext>
    );
};

export const SetEditingSortableContext = ({
    children,
    items,
    ...divProps
}: {
    children: React.ReactNode;
    items: FlashcardSortable[];
} & React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <FlexColumn {...divProps}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </FlexColumn>
    );
};

const SetEditingSortableNodeContext = createContext<{
    listeners?: SyntheticListenerMap;
    attributes?: DraggableAttributes;
}>({});

export const SetEditingSortableNode = ({
    id,
    children,
    item,
}: {
    id: string;
    children: React.ReactNode;
    item: FlashcardSortable;
}) => {
    const { smDown } = useBreakPoints();

    const { listeners, setNodeRef, attributes, transform, transition, isDragging } = useSortable({
        transition: { duration: 250, easing: "cubic-bezier(0.25, 1, 0.5, 1)" },
        data: item,
        id,
        animateLayoutChanges: () => true,
        disabled: smDown,
    });

    // so that it's not squished when A card's height is different from B card's height
    if (transform) {
        transform.scaleX = 1;
        transform.scaleY = 1;
    }

    return (
        <SetEditingSortableNodeContext.Provider value={{ listeners, attributes }}>
            <div
                ref={setNodeRef}
                id={id}
                style={{
                    opacity: isDragging ? 0 : 1,
                    transform: CSS.Transform.toString(transform),
                    transition,
                }}>
                {children}
            </div>
        </SetEditingSortableNodeContext.Provider>
    );
};

/**
 * The one used for drag handler. The "drag cursor"
 */
export const SetEditingSortableHandler = ({ children }) => {
    const { listeners, attributes } = useContext(SetEditingSortableNodeContext);

    return (
        <div style={{ cursor: "drag" }} {...listeners} {...attributes} tabIndex={-1}>
            {children}
        </div>
    );
};
